export const HttpMethods = {
	CONNECT: 'CONNECT',
	DELETE: 'DELETE',
	GET: 'GET',
	HEAD: 'HEAD',
	OPTIONS: 'OPTIONS',
	PATCH: 'PATCH',
	POST: 'POST',
	PUT: 'PUT',
	TRACE: 'TRACE'
} as const;

type HttpMethod = keyof typeof HttpMethods;

export const APIResponseStatuses = {
	SUCCESS: 'SUCCESS',
	FAILURE: 'FAILURE'
} as const;

type APIResponseStatus = keyof typeof APIResponseStatuses;

export type BaseResponse<T = undefined> =
	| {
			status: 'SUCCESS';
			result?: T;
	  }
	| {
			status: 'FAILURE';
			errorCode: string;
			errorDescription: string;
			result?: unknown;
	  };

const API_BASE_PATH = process.env.API_BASE_PATH || '';

type GetFetchHeadersArgs = {
	payload?: unknown;
	method: HttpMethod;
	urlencodedContentType: boolean;
	netellerHeaders: boolean;
	additionalHeaders: HeadersInit;
	additionalOptions: RequestInit;
};

function getFetchHeaders({
	payload = {},
	method,
	urlencodedContentType,
	netellerHeaders,
	additionalHeaders,
	additionalOptions
}: GetFetchHeadersArgs) {
	const appJson = 'application/json; charset=utf-8';
	const appUrle = 'application/x-www-form-urlencoded; charset=UTF-8';
	const contentType = urlencodedContentType ? appUrle : appJson;
	const optionsDef: RequestInit = {
		headers: {
			...(payload instanceof FormData ? {} : { 'Content-Type': contentType })
		},
		cache: 'no-store',
		credentials: 'include'
	};
	const optionsNeteller: RequestInit = {
		headers: {
			...(payload instanceof FormData ? {} : { 'Content-Type': appJson }),
			'Cache-Control': 'no-cache, no-store, must-revalidate',
			Accept: 'application/json, text/javascript, */*; q=0.01',
			'X-Requested-With': 'XMLHttpRequest',
			Expires: '0',
			'Accept-Encoding': 'gzip, deflate, br'
		},
		credentials: 'include'
	};
	let options = netellerHeaders ? optionsNeteller : optionsDef;

	if (additionalHeaders) {
		options = {
			...options,
			headers: {
				...options.headers,
				...additionalHeaders
			}
		};
	}
	if (additionalOptions) {
		options = {
			...options,
			...additionalOptions
		};
	}
	if (method === 'GET' || method === 'HEAD') {
		return options;
	}

	// TODO: if the content-type is urlencoded, the payload is a urlencoded string
	// like key1=value1&key2=value2&key3=value3
	// This may be handled here or elsewhere, or show warning.

	const body = typeof payload === 'string' || payload instanceof FormData ? payload : JSON.stringify(payload);
	// in case of the payload is a string, the JSON.stringify will wrap
	// the string inside extra quotations.
	// JSON.stringify("foo") === ""foo"
	// body: JSON.stringify(payload)

	return {
		...options,
		...{
			method,
			body
		}
	};
}

type DoAPIRequestArgs = {
	endpoint?: string;
	payload?: unknown;
	method?: HttpMethod;
	urlencodedContentType?: boolean;
	netellerHeaders?: boolean;
	additionalHeaders?: HeadersInit;
	additionalOptions?: RequestInit;
};

export const doAPIRequest = ({
	endpoint = '/',
	payload = {},
	method = HttpMethods.GET,
	urlencodedContentType = false,
	netellerHeaders = false,
	additionalHeaders = {},
	additionalOptions = {}
}: DoAPIRequestArgs) => {
	const headers = getFetchHeaders({
		payload,
		method,
		urlencodedContentType,
		netellerHeaders,
		additionalHeaders,
		additionalOptions
	});
	return fetch(`${API_BASE_PATH}${endpoint}`, headers);
};

export type RequestApiArgs = {
	endpoint?: string;
	payload?: unknown;
	method?: HttpMethod;
	urlencodedContentType?: boolean;
	netellerHeaders?: boolean;
	additionalHeaders?: HeadersInit;
	additionalOptions?: RequestInit;
	logResponse?: boolean;
};

async function requestApi<T>({
	endpoint = '/',
	payload = {},
	method = HttpMethods.GET,
	urlencodedContentType = false,
	netellerHeaders = false,
	additionalHeaders = {},
	additionalOptions = {}
}: RequestApiArgs) {
	let response;
	try {
		response = await doAPIRequest({
			endpoint,
			payload,
			method,
			urlencodedContentType,
			netellerHeaders,
			additionalHeaders,
			additionalOptions
		});
		return (await response.json()) as T;
	} catch (err) {
		throw new Error(`${endpoint}, ${JSON.stringify(err)}`, { cause: err });
	}
}

type ParseResponseArgs<T> = {
	status?: APIResponseStatus;
	errorCode?: string;
	errorDescription?: string;
	result?: T;
};

const parseResponse = <T>({ status, errorCode = '', errorDescription = '', result }: ParseResponseArgs<T>) =>
	result || { error: { errorCode, errorDescription, status } };

export { requestApi, parseResponse };
